import { createContext, useContext, useState, useEffect } from 'react';
import { useAdd, useFetchObjects } from '../components/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { handleAdd: loginRequest, isLoading: isLoginLoading, isSuccess: isLoginSuccess } = useAdd('auth/login');
  const { data: userData, refetch: verifyUser } = useFetchObjects(
    ['user'], 
    'auth/profile', 
    { enabled: !!token }
  );

  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      verifyUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userData) {
      setUser(userData);
      setIsLoading(false);
    }
  }, [userData]);

  const login = async (email, password) => {
    try {
      const response = await loginRequest({ email, password });
      const { token: authToken, user: userData } = response;
      
      setToken(authToken);
      setUser(userData);
      sessionStorage.setItem('token', authToken);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Login failed' };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    sessionStorage.removeItem('token');
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const value = {
    user,
    token,
    isLoading,
    isLoginLoading,
    isLoginSuccess,
    login,
    logout,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};